import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where, type DocumentReference } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import type { CutierLead } from '../store/useCutierStore';
import {
  parseBulkEmailText,
  validateCutierEmail,
  checkEmailDomainDeliverable,
  filterDeliverableLeads,
  CUTIER_EMAIL_REGEX,
} from '../utils/cutier/cutierEmailParse';
import { cutierLeadDocId } from '../utils/cutier/cutierLeadUtils';
import {
  CUTIER_INVOICE_PROFILE_TEMPLATE,
  type CutierInvoiceProfile,
} from '../types/cutierInvoiceProfile';

const CUTIER_LEADS_COLLECTION = 'cutier_seminar_leads';
/** Partner-Hub Bearbeitungs-PIN (Login /cutier) — fest im Betrieb. */
const FALLBACK_PIN = 'cutier2026';
const FALLBACK_DELETE_PIN = 'C26';
const FALLBACK_ADMIN_PIN = '90';
/** Ramin Dell — persönlich, nicht die Salon-Adresse. Antworten gehen hierhin. */
export const CUTIER_RAMIN_EMAIL = 'rd@ramindell.com';
export const FALLBACK_SEMINAR_URL = 'https://ramindell.com/online-seminar';
export const FALLBACK_RATE_GROSS = 50;
const LOCAL_SETTINGS_KEY = 'cutier_admin_settings';
const LOCAL_INVOICE_PROFILE_KEY = 'cutier_invoice_profile';

/** Master-Passwort — immer gültig (Löschen, Korrektur, Admin), unabhängig von Partner-PINs. */
const isMasterAdminPassword = (pin: string): boolean => pin === FALLBACK_ADMIN_PIN;

export const verifyCutierPin = async (enteredPin: string): Promise<boolean> => {
  const pin = String(enteredPin || '').trim();
  if (!pin) return false;
  if (isMasterAdminPassword(pin)) return true;
  // Hub-PIN ist cutier2026 — immer gültig, unabhängig von Firestore/Seminar-Feldern
  if (pin === FALLBACK_PIN) return true;
  try {
    const snap = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (!snap.exists()) return false;
    const storedPin = String((snap.data() as { pin?: string })?.pin || '').trim();
    return Boolean(storedPin && pin === storedPin);
  } catch {
    return false;
  }
};

export const verifyCutierDeletePin = async (enteredPin: string): Promise<boolean> => {
  const pin = String(enteredPin || '').trim();
  if (!pin) return false;
  if (isMasterAdminPassword(pin)) return true;
  if (pin === FALLBACK_DELETE_PIN) return true;
  try {
    const snap = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (!snap.exists()) return false;
    const stored = String((snap.data() as { deletePin?: string })?.deletePin || '').trim();
    return Boolean(stored && pin === stored);
  } catch {
    return pin === FALLBACK_DELETE_PIN;
  }
};

export const verifyCutierAdminPin = async (enteredPin: string): Promise<boolean> => {
  const pin = String(enteredPin || '').trim();
  if (!pin) return false;
  if (isMasterAdminPassword(pin)) return true;
  try {
    const snap = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (!snap.exists()) return false;
    const stored = String((snap.data() as { adminPin?: string })?.adminPin || '').trim();
    return Boolean(stored && pin === stored);
  } catch {
    return false;
  }
};

/** Korrektur Name/E-Mail — Master 90, Hub-, Lösch- oder Admin-PIN. */
export const verifyCutierEditPin = async (enteredPin: string): Promise<boolean> => {
  const pin = String(enteredPin || '').trim();
  if (!pin) return false;
  if (isMasterAdminPassword(pin)) return true;
  if (pin === FALLBACK_PIN || pin === FALLBACK_DELETE_PIN) return true;
  const [adminOk, hubOk, deleteOk] = await Promise.all([
    verifyCutierAdminPin(pin),
    verifyCutierPin(pin),
    verifyCutierDeletePin(pin),
  ]);
  return adminOk || hubOk || deleteOk;
};

export interface CutierAdminSettings {
  rateGross: number;
  seminarUrl: string;
  seminarPassword: string;
}

const mergeInvoiceProfile = (raw?: Partial<CutierInvoiceProfile> | null): CutierInvoiceProfile => ({
  ...CUTIER_INVOICE_PROFILE_TEMPLATE,
  ...(raw || {}),
  defaultPaymentTermsDays:
    typeof raw?.defaultPaymentTermsDays === 'number' && raw.defaultPaymentTermsDays > 0
      ? raw.defaultPaymentTermsDays
      : CUTIER_INVOICE_PROFILE_TEMPLATE.defaultPaymentTermsDays,
});

/** Firestore lehnt undefined ab — nur bekannte, serialisierbare Felder schreiben. */
export function sanitizeCutierInvoiceProfile(
  raw?: Partial<CutierInvoiceProfile> | null
): CutierInvoiceProfile {
  const merged = mergeInvoiceProfile(raw);
  return {
    displayName: String(merged.displayName || '').trim(),
    accountHolder: String(merged.accountHolder || merged.displayName || '').trim(),
    bankName: String(merged.bankName || '').trim(),
    iban: String(merged.iban || '').replace(/\s+/g, '').toUpperCase(),
    bic: String(merged.bic || '').replace(/\s+/g, '').toUpperCase(),
    taxNumber: String(merged.taxNumber || '').trim(),
    vatId: String(merged.vatId || '').trim(),
    street: String(merged.street || '').trim(),
    zip: String(merged.zip || '').trim(),
    city: String(merged.city || '').trim(),
    country: String(merged.country || 'DE').trim().toUpperCase() || 'DE',
    email: String(merged.email || '').trim(),
    phone: String(merged.phone || '').trim(),
    buyerCompanyName: String(merged.buyerCompanyName || '').trim(),
    buyerContactPerson: String(merged.buyerContactPerson || '').trim(),
    buyerEmail: String(merged.buyerEmail || '').trim().toLowerCase(),
    buyerStreet: String(merged.buyerStreet || '').trim(),
    buyerZip: String(merged.buyerZip || '').trim(),
    buyerCity: String(merged.buyerCity || '').trim(),
    buyerCountry: String(merged.buyerCountry || 'DE').trim().toUpperCase() || 'DE',
    buyerVatId: String(merged.buyerVatId || '').trim(),
    defaultPaymentTermsDays:
      typeof merged.defaultPaymentTermsDays === 'number' && merged.defaultPaymentTermsDays > 0
        ? merged.defaultPaymentTermsDays
        : 14,
  };
}

const profileHasBankData = (p?: Partial<CutierInvoiceProfile> | null) =>
  Boolean(String(p?.iban || '').trim() || String(p?.accountHolder || '').trim() || String(p?.bankName || '').trim());

const readLocalInvoiceProfile = (): CutierInvoiceProfile | null => {
  try {
    const raw = localStorage.getItem(LOCAL_INVOICE_PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { profile?: Partial<CutierInvoiceProfile> } | Partial<CutierInvoiceProfile>;
    if (!parsed || typeof parsed !== 'object') return null;
    const nested = 'profile' in parsed ? (parsed as { profile?: Partial<CutierInvoiceProfile> }).profile : parsed;
    if (!nested || typeof nested !== 'object') return null;
    return sanitizeCutierInvoiceProfile(nested);
  } catch {
    return null;
  }
};

const writeLocalInvoiceProfile = (profile: CutierInvoiceProfile) => {
  try {
    localStorage.setItem(LOCAL_INVOICE_PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // private mode / quota
  }
};

export async function loadCutierInvoiceProfile(): Promise<CutierInvoiceProfile> {
  const local = readLocalInvoiceProfile();
  try {
    const snap = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (snap.exists()) {
      const data = snap.data() as { invoiceProfile?: Partial<CutierInvoiceProfile> };
      if (data.invoiceProfile && typeof data.invoiceProfile === 'object') {
        const remote = sanitizeCutierInvoiceProfile(data.invoiceProfile);
        if (profileHasBankData(remote)) {
          writeLocalInvoiceProfile(remote);
          return remote;
        }
      }
    }
  } catch {
    // local/template fallback
  }
  if (local && profileHasBankData(local)) {
    void saveCutierInvoiceProfile(local);
    return local;
  }
  return { ...CUTIER_INVOICE_PROFILE_TEMPLATE };
}

export async function saveCutierInvoiceProfile(
  profile: CutierInvoiceProfile
): Promise<{ savedRemote: boolean; error?: string }> {
  const clean = sanitizeCutierInvoiceProfile(profile);
  writeLocalInvoiceProfile(clean);
  try {
    await setDoc(
      doc(db, 'cutier_settings', 'default'),
      {
        invoiceProfile: clean,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
    return { savedRemote: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err || '');
    return { savedRemote: false, error: message };
  }
}

const readLocalSettings = (): Partial<CutierAdminSettings> => {
  try {
    const raw = localStorage.getItem(LOCAL_SETTINGS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { rateGross?: number; seminarUrl?: string; seminarPassword?: string };
    return {
      rateGross: typeof parsed.rateGross === 'number' ? parsed.rateGross : undefined,
      seminarUrl: typeof parsed.seminarUrl === 'string' ? parsed.seminarUrl : undefined,
      seminarPassword: typeof parsed.seminarPassword === 'string' ? parsed.seminarPassword : undefined,
    };
  } catch {
    return {};
  }
};

export async function loadCutierAdminSettings(): Promise<CutierAdminSettings> {
  const local = readLocalSettings();
  let rateGross = local.rateGross && local.rateGross > 0 ? local.rateGross : FALLBACK_RATE_GROSS;
  let seminarUrl = String(local.seminarUrl || '').trim() || FALLBACK_SEMINAR_URL;
  let seminarPassword = String(local.seminarPassword || '').trim();
  try {
    const snap = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (snap.exists()) {
      const data = snap.data() as { rateGross?: number; seminarUrl?: string; seminarPassword?: string };
      if (typeof data.rateGross === 'number' && data.rateGross > 0) rateGross = data.rateGross;
      const url = String(data.seminarUrl || '').trim();
      if (url) seminarUrl = url;
      if (data.seminarPassword != null) seminarPassword = String(data.seminarPassword).trim();
    }
  } catch {
    // local/fallback
  }
  return { rateGross, seminarUrl, seminarPassword };
}

export async function saveCutierAdminSettings(
  settings: CutierAdminSettings
): Promise<{ savedRemote: boolean }> {
  try {
    localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
  try {
    await setDoc(
      doc(db, 'cutier_settings', 'default'),
      {
        rateGross: settings.rateGross,
        seminarUrl: settings.seminarUrl,
        seminarPassword: settings.seminarPassword,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
    return { savedRemote: true };
  } catch {
    return { savedRemote: false };
  }
}

export const deleteLeadsById = async (ids: string[]): Promise<{ deletedCount: number; failedCount: number }> => {
  let deletedCount = 0;
  let failedCount = 0;
  for (const raw of ids) {
    const id = String(raw || '').trim();
    if (!id) continue;
    try {
      await deleteDoc(doc(db, CUTIER_LEADS_COLLECTION, id));
      deletedCount++;
    } catch {
      failedCount++;
    }
  }
  return { deletedCount, failedCount };
};

/** @deprecated Nutze deleteLeadsById mit lead.id */
export const deleteLeadsByEmail = deleteLeadsById;

const mapLead = (data: Record<string, unknown>, docId: string): CutierLead => {
  const orderCountRaw = data.orderCount;
  const orderCount =
    typeof orderCountRaw === 'number' && orderCountRaw > 0 ? Math.floor(orderCountRaw) : 1;
  return {
    id: docId,
    email: String(data.email || docId.split('__').pop() || '').trim().toLowerCase(),
    name: data.name != null ? String(data.name) : undefined,
    orderCount,
    createdAt: String(data.createdAt || ''),
    updatedAt: data.updatedAt != null ? String(data.updatedAt) : undefined,
    monthKey: String(data.monthKey || ''),
    invite_sent: data.invite_sent === true,
    newsletterOptIn:
      data.newsletterOptIn === 'pending' || data.newsletterOptIn === 'confirmed'
        ? data.newsletterOptIn
        : 'none',
    source: data.source === 'bulk' ? 'bulk' : 'single',
  };
};

async function findExistingLeadRef(
  monthKey: string,
  email: string
): Promise<{ ref: DocumentReference; data: CutierLead } | null> {
  const compositeRef = doc(db, CUTIER_LEADS_COLLECTION, cutierLeadDocId(monthKey, email));
  const compositeSnap = await getDoc(compositeRef);
  if (compositeSnap.exists()) {
    return {
      ref: compositeRef,
      data: mapLead((compositeSnap.data() || {}) as Record<string, unknown>, compositeSnap.id),
    };
  }

  const legacyRef = doc(db, CUTIER_LEADS_COLLECTION, email);
  const legacySnap = await getDoc(legacyRef);
  if (legacySnap.exists()) {
    const data = mapLead((legacySnap.data() || {}) as Record<string, unknown>, legacySnap.id);
    if (data.monthKey === monthKey) {
      return { ref: legacyRef, data };
    }
  }

  return null;
}

/**
 * Name und/oder E-Mail eines Leads korrigieren.
 * E-Mail-Wechsel: neues Doc (ID = Monat__E-Mail), altes löschen — Rules erlauben create/delete offen.
 */
export const updateCutierLeadContact = async (opts: {
  leadId: string;
  monthKey: string;
  email: string;
  name?: string;
}): Promise<{ ok: boolean; lead?: CutierLead; error?: string }> => {
  const leadId = String(opts.leadId || '').trim();
  const monthKey = String(opts.monthKey || '').trim();
  const email = String(opts.email || '').trim().toLowerCase();
  const name = opts.name != null ? String(opts.name).trim() : '';

  if (!leadId || !monthKey) {
    return { ok: false, error: 'Lead ungültig.' };
  }
  if (!email || !CUTIER_EMAIL_REGEX.test(email)) {
    return { ok: false, error: 'E-Mail-Adresse ungültig.' };
  }

  const oldRef = doc(db, CUTIER_LEADS_COLLECTION, leadId);
  let oldSnap;
  try {
    oldSnap = await getDoc(oldRef);
  } catch {
    return { ok: false, error: 'Lead konnte nicht geladen werden.' };
  }
  if (!oldSnap.exists()) {
    return { ok: false, error: 'Lead nicht gefunden.' };
  }

  const oldData = (oldSnap.data() || {}) as Record<string, unknown>;
  const oldEmail = String(oldData.email || '').trim().toLowerCase();
  const nowIso = new Date().toISOString();
  const emailChanged = email !== oldEmail;

  if (!emailChanged) {
    try {
      await updateDoc(oldRef, {
        name: name || null,
        updatedAt: nowIso,
      });
      return {
        ok: true,
        lead: mapLead({ ...oldData, name: name || undefined, updatedAt: nowIso }, leadId),
      };
    } catch {
      return { ok: false, error: 'Speichern fehlgeschlagen (Firestore).' };
    }
  }

  const newId = cutierLeadDocId(monthKey, email);
  if (newId === leadId) {
    try {
      await updateDoc(oldRef, {
        email,
        name: name || null,
        updatedAt: nowIso,
      });
      return {
        ok: true,
        lead: mapLead({ ...oldData, email, name: name || undefined, updatedAt: nowIso }, leadId),
      };
    } catch {
      return { ok: false, error: 'E-Mail konnte nicht aktualisiert werden (Firestore-Rules).' };
    }
  }

  const conflict = await findExistingLeadRef(monthKey, email);
  if (conflict && conflict.ref.id !== leadId) {
    return { ok: false, error: `E-Mail ${email} existiert in diesem Monat bereits.` };
  }

  const payload: Record<string, unknown> = {
    ...oldData,
    email,
    name: name || null,
    monthKey,
    updatedAt: nowIso,
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  try {
    await setDoc(doc(db, CUTIER_LEADS_COLLECTION, newId), payload);
    try {
      await deleteDoc(oldRef);
    } catch {
      return {
        ok: false,
        error: 'Neue Adresse angelegt, altes Dokument konnte nicht gelöscht werden.',
      };
    }
    return {
      ok: true,
      lead: mapLead(payload, newId),
    };
  } catch {
    return { ok: false, error: 'E-Mail-Korrektur fehlgeschlagen (Firestore).' };
  }
};

interface UpsertLeadParams {
  email: string;
  name?: string;
  monthKey: string;
  orderCount: number;
  source: 'single' | 'bulk';
  mailContext?: { seminarUrl: string; seminarPassword: string };
}

async function upsertCutierLead(params: UpsertLeadParams): Promise<{
  isNew: boolean;
  mailQueued: boolean;
  newOrderCount: number;
}> {
  const normalizedEmail = params.email.toLowerCase().trim();
  const addUnits = Math.max(1, Math.floor(params.orderCount));
  const trimmedName = params.name?.trim() || '';
  const now = new Date().toISOString();
  const existing = await findExistingLeadRef(params.monthKey, normalizedEmail);

  if (existing) {
    const newOrderCount = existing.data.orderCount + addUnits;
    const patch: Record<string, unknown> = {
      orderCount: newOrderCount,
      updatedAt: now,
    };
    if (trimmedName && !existing.data.name?.trim()) {
      patch.name = trimmedName;
    }
    await updateDoc(existing.ref, patch);
    return { isNew: false, mailQueued: false, newOrderCount };
  }

  const targetRef = doc(db, CUTIER_LEADS_COLLECTION, cutierLeadDocId(params.monthKey, normalizedEmail));
  const mailSuccess = await queueSeminarMail(
    normalizedEmail,
    trimmedName || undefined,
    params.mailContext
  );

  const newLead: Omit<CutierLead, 'id'> = {
    email: normalizedEmail,
    name: trimmedName || undefined,
    orderCount: addUnits,
    createdAt: now,
    updatedAt: now,
    monthKey: params.monthKey,
    invite_sent: mailSuccess,
    newsletterOptIn: 'none',
    source: params.source,
  };

  await setDoc(targetRef, newLead);
  return { isNew: true, mailQueued: mailSuccess, newOrderCount: addUnits };
}

export const setLeadOrderCount = async (leadId: string, orderCount: number): Promise<void> => {
  const id = String(leadId || '').trim();
  const units = Math.max(1, Math.min(99, Math.floor(orderCount)));
  if (!id) throw new Error('Lead fehlt.');
  await updateDoc(doc(db, CUTIER_LEADS_COLLECTION, id), {
    orderCount: units,
    updatedAt: new Date().toISOString(),
  });
};

export const fetchLeadsByMonth = async (monthKey: string): Promise<CutierLead[]> => {
  const q = query(collection(db, CUTIER_LEADS_COLLECTION), where('monthKey', '==', monthKey));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapLead((d.data() || {}) as Record<string, unknown>, d.id));
};

export interface IngestionResult {
  addedCount: number;
  /** Zusätzliche Bestellungen bei bereits erfassten Adressen */
  repeatOrderCount: number;
  /** @deprecated Alias für repeatOrderCount */
  duplicateCount: number;
  invalidCount: number;
  duplicates: string[];
  invalidEntries: { raw: string; reason: string }[];
  mailFailedCount: number;
  failedEntries: { email: string; reason: string }[];
}

/** @deprecated Alias — bitte CUTIER_EMAIL_REGEX aus cutierEmailParse nutzen */
export const EMAIL_REGEX = CUTIER_EMAIL_REGEX;

async function loadSeminarMailContext(): Promise<{ seminarUrl: string; seminarPassword: string }> {
  const { useCutierStore } = await import('../store/useCutierStore');
  const state = useCutierStore.getState();
  let seminarUrl = String(state.seminarUrl || '').trim() || FALLBACK_SEMINAR_URL;
  let seminarPassword = String(state.seminarPassword || '').trim();
  try {
    const settingsDoc = await getDoc(doc(db, 'cutier_settings', 'default'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as { seminarUrl?: string; seminarPassword?: string };
      const storedUrl = String(data.seminarUrl || '').trim();
      if (storedUrl) seminarUrl = storedUrl;
      if (data.seminarPassword != null) seminarPassword = String(data.seminarPassword).trim();
    }
  } catch {
    // Store/Fallback greift
  }
  return { seminarUrl, seminarPassword };
}

const cutierAppBaseUrl = () =>
  typeof window !== 'undefined' ? window.location.origin : 'https://pe-one.friseur-coaching.de';

/** Firestore-Dokumente max. ~1 MiB — Base64-PDF + HTML müssen darunter bleiben. */
const MAIL_QUEUE_PDF_BASE64_MAX = 700_000;

async function uploadCutierInvoicePdf(opts: {
  pdfBytes: Uint8Array;
  fileName: string;
  monthKey?: string;
}): Promise<{ pdfUrl: string; storagePath: string }> {
  const safeName = String(opts.fileName || 'Rechnung.pdf')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 120);
  const month = String(opts.monthKey || 'offen').replace(/[^\d-]/g, '') || 'offen';
  const storagePath = `cutier_invoices/${month}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, opts.pdfBytes, { contentType: 'application/pdf' });
  const pdfUrl = await getDownloadURL(storageRef);
  return { pdfUrl, storagePath };
}

/**
 * Versand über processMailQueue (type change_request = generischer HTML-Versand).
 * WICHTIG: `mailTitle` ist die sichtbare Überschrift im Mail-Rahmen — ohne dieses
 * Feld druckt der Worker seinen Fallback „Change-Radar“. Genau das Feld hat gefehlt.
 */
export async function sendCutierHtmlMail(opts: {
  to: string;
  name?: string;
  subject: string;
  html: string;
  text: string;
  seminarUrl?: string;
  mailTitle?: string;
  pdfBase64?: string;
  replyTo?: string;
  mailType?: string;
}): Promise<boolean> {
  const result = await sendCutierHtmlMailDetailed(opts);
  return result.ok;
}

export async function sendCutierHtmlMailDetailed(opts: {
  to: string;
  name?: string;
  subject: string;
  html: string;
  text: string;
  seminarUrl?: string;
  mailTitle?: string;
  pdfBase64?: string;
  replyTo?: string;
  /** undefined = wie Seminar-Rechnung (kein type); sonst z. B. change_request */
  mailType?: string | null;
  invoiceNr?: string;
  pdfUrl?: string;
  eventName?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const to = opts.to.toLowerCase().trim();
  if (!to || !to.includes('@')) {
    return { ok: false, error: 'Empfänger-E-Mail fehlt oder ist ungültig.' };
  }

  try {
    let pdfBase64 = opts.pdfBase64 ? String(opts.pdfBase64) : '';
    if (pdfBase64 && pdfBase64.length > MAIL_QUEUE_PDF_BASE64_MAX) {
      console.warn(
        `[Cutier] pdfBase64 ${pdfBase64.length} Zeichen > ${MAIL_QUEUE_PDF_BASE64_MAX} — Queue ohne Inline-Anhang (Storage-Link)`
      );
      pdfBase64 = '';
    }

    // Schema wie AdminInvoiceCreator / Coach bei Anhang; sonst change_request für HTML-Mail
    const payload: Record<string, unknown> = {
      email: to,
      replyTo: opts.replyTo || CUTIER_RAMIN_EMAIL,
      mailTitle: opts.mailTitle || 'Lockenzeremonie & CUTIER',
      subject: opts.subject,
      html: opts.html,
      messageHtml: opts.html,
      text: opts.text,
      message: opts.text,
      participantName: opts.name?.trim() || 'Seminar-Teilnehmer',
      salonName: 'Lockenzeremonie & CUTIER',
      eventName: opts.eventName || opts.subject,
      appBaseUrl: cutierAppBaseUrl(),
      status: 'pending',
      createdAt: serverTimestamp(),
    };
    if (opts.mailType) payload.type = opts.mailType;
    else if (!pdfBase64) payload.type = 'change_request';
    if (opts.invoiceNr) payload.invoiceNr = opts.invoiceNr;
    if (opts.pdfUrl) payload.pdfUrl = opts.pdfUrl;
    if (pdfBase64) payload.pdfBase64 = pdfBase64;

    await addDoc(collection(db, 'mail_queue'), payload);
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err || 'Unbekannter Fehler');
    console.error('Cutier-Mail (mail_queue) fehlgeschlagen:', err);
    return { ok: false, error: message };
  }
}

function pdfBytesToDataUri(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return `data:application/pdf;base64,${btoa(binary)}`;
}

/**
 * Rechnung an CUTIER — große E-Rechnungen (> Firestore-1-MB) über Storage,
 * kleiner Anhang zusätzlich wie bei Coach/Seminar, wenn er reinpasst.
 */
export async function enqueueCutierInvoiceMail(opts: {
  to: string;
  name?: string;
  subject: string;
  html: string;
  text: string;
  mailTitle?: string;
  replyTo?: string;
  invoiceNr: string;
  fileName: string;
  monthKey: string;
  pdfBytes: Uint8Array;
  pdfBase64?: string;
}): Promise<{ ok: boolean; error?: string; usedAttachment: boolean; pdfUrl?: string }> {
  const to = opts.to.toLowerCase().trim();
  if (!to || !to.includes('@')) {
    return { ok: false, error: 'Empfänger-E-Mail fehlt (Rechnungsdaten → Empfänger).', usedAttachment: false };
  }

  let pdfUrl = '';
  let uploadError = '';
  try {
    const uploaded = await uploadCutierInvoicePdf({
      pdfBytes: opts.pdfBytes,
      fileName: opts.fileName,
      monthKey: opts.monthKey,
    });
    pdfUrl = uploaded.pdfUrl;
  } catch (err) {
    uploadError = err instanceof Error ? err.message : String(err || '');
    console.error('[Cutier] PDF-Upload fehlgeschlagen:', err);
  }

  const attachBase64 =
    opts.pdfBase64 ||
    (opts.pdfBytes?.length ? pdfBytesToDataUri(opts.pdfBytes) : '');
  const canInlineAttach = Boolean(attachBase64 && attachBase64.length <= MAIL_QUEUE_PDF_BASE64_MAX);

  if (!pdfUrl && !canInlineAttach) {
    return {
      ok: false,
      error: uploadError
        ? `PDF zu groß für direkten Versand und Upload fehlgeschlagen (${uploadError}).`
        : 'PDF zu groß für direkten Versand und kein Storage-Link verfügbar.',
      usedAttachment: false,
    };
  }

  const downloadBlock = pdfUrl
    ? `<p style="margin:20px 0"><a href="${pdfUrl}" style="display:inline-block;background:#E6C299;color:#111;font-weight:700;text-decoration:none;padding:12px 18px;border-radius:8px">E-Rechnung / PDF herunterladen</a></p>`
    : '';
  const html = `${opts.html}${downloadBlock}`;
  const text = pdfUrl ? `${opts.text}\n\nPDF / E-Rechnung: ${pdfUrl}\n` : opts.text;

  const result = await sendCutierHtmlMailDetailed({
    to,
    name: opts.name,
    subject: opts.subject,
    html,
    text,
    mailTitle: opts.mailTitle,
    replyTo: opts.replyTo || CUTIER_RAMIN_EMAIL,
    mailType: canInlineAttach ? null : 'change_request',
    invoiceNr: opts.invoiceNr,
    eventName: opts.subject,
    pdfUrl: pdfUrl || undefined,
    pdfBase64: canInlineAttach ? attachBase64 : undefined,
  });

  return {
    ok: result.ok,
    error: result.error,
    usedAttachment: canInlineAttach,
    pdfUrl: pdfUrl || undefined,
  };
}

export async function queueSeminarMail(
  email: string,
  name?: string,
  mailContext?: { seminarUrl: string; seminarPassword: string }
): Promise<boolean> {
  try {
    const { seminarUrl, seminarPassword } = mailContext ?? (await loadSeminarMailContext());

    const { buildSeminarInviteMail } = await import('../utils/cutier/cutierInviteMail');
    const mail = buildSeminarInviteMail({
      email,
      name,
      seminarUrl,
      seminarPassword,
    });

    return sendCutierHtmlMail({
      to: email,
      name,
      subject: mail.subject,
      html: mail.html,
      text: mail.text,
      seminarUrl,
    });
  } catch (err) {
    console.error('Cutier-Einladung fehlgeschlagen:', err);
    return false;
  }
}

/** Einladungs-Mail erneut in die Queue legen (z. B. nach E-Mail-Korrektur). */
export async function resendCutierInviteMail(opts: {
  leadId: string;
  email: string;
  name?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const email = String(opts.email || '').trim().toLowerCase();
  const leadId = String(opts.leadId || '').trim();
  if (!email || !email.includes('@')) {
    return { ok: false, error: 'Keine gültige E-Mail an diesem Teilnehmer.' };
  }
  if (!leadId) {
    return { ok: false, error: 'Lead-ID fehlt.' };
  }

  const ok = await queueSeminarMail(email, opts.name);
  if (!ok) {
    return { ok: false, error: 'Mail konnte nicht in die Warteschlange gestellt werden.' };
  }

  try {
    await updateDoc(doc(db, CUTIER_LEADS_COLLECTION, leadId), {
      invite_sent: true,
      updatedAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('[Cutier] invite_sent nach Resend nicht gesetzt:', err);
  }

  return { ok: true };
}

export async function saveSingleLead(
  email: string,
  name: string | undefined,
  monthKey: string,
  orderCount = 1
): Promise<{
  success: boolean;
  isRepeatOrder?: boolean;
  newOrderCount?: number;
  invalidReason?: string;
}> {
  const check = validateCutierEmail(email);
  if (!check.ok || !check.email) {
    throw new Error(check.reason || 'Ungültige oder unzustellbare E-Mail-Adresse.');
  }
  const normalizedEmail = check.email;

  const deliverable = await checkEmailDomainDeliverable(normalizedEmail);
  if (!deliverable.ok) {
    throw new Error(
      deliverable.reason ||
        'Diese Domain kann keine E-Mails empfangen — bitte Adresse korrigieren.'
    );
  }

  const units = Math.max(1, Math.floor(orderCount));
  const result = await upsertCutierLead({
    email: normalizedEmail,
    name,
    monthKey,
    orderCount: units,
    source: 'single',
  });

  if (!result.isNew && !result.mailQueued) {
    return {
      success: true,
      isRepeatOrder: true,
      newOrderCount: result.newOrderCount,
    };
  }

  return { success: true, newOrderCount: result.newOrderCount };
}

export async function saveCorrectedLeads(
  entries: { email: string; name?: string; orderCount?: number }[],
  monthKey: string,
  onProgress?: (done: number, total: number) => void
): Promise<{
  savedCount: number;
  repeatOrderCount: number;
  mailFailedCount: number;
  stillInvalid: { raw: string; email: string; name?: string; reason: string }[];
}> {
  const mailContext = await loadSeminarMailContext();
  const total = entries.length;
  let savedCount = 0;
  let repeatOrderCount = 0;
  let mailFailedCount = 0;
  const stillInvalid: { raw: string; email: string; name?: string; reason: string }[] = [];

  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    onProgress?.(i + 1, total);

    const check = validateCutierEmail(entry.email);
    if (!check.ok || !check.email) {
      stillInvalid.push({
        raw: entry.email,
        email: entry.email,
        name: entry.name,
        reason: check.reason || 'Ungültige Adresse.',
      });
      continue;
    }

    const deliverable = await checkEmailDomainDeliverable(check.email);
    if (!deliverable.ok) {
      stillInvalid.push({
        raw: entry.email,
        email: entry.email,
        name: entry.name,
        reason: deliverable.reason || 'Domain nicht zustellbar.',
      });
      continue;
    }

    try {
      const units = Math.max(1, Math.floor(entry.orderCount ?? 1));
      const result = await upsertCutierLead({
        email: check.email,
        name: entry.name,
        monthKey,
        orderCount: units,
        source: 'single',
        mailContext,
      });
      savedCount += units;
      if (!result.isNew) repeatOrderCount += units;
      if (result.isNew && !result.mailQueued) mailFailedCount++;
    } catch (err) {
      stillInvalid.push({
        raw: entry.email,
        email: entry.email,
        name: entry.name,
        reason: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.',
      });
    }
  }

  return { savedCount, repeatOrderCount, mailFailedCount, stillInvalid };
}

export async function saveBulkLeads(
  rawText: string,
  monthKey: string,
  onProgress?: (done: number, total: number) => void
): Promise<IngestionResult> {
  const parsed = parseBulkEmailText(rawText);
  const { deliverable, undeliverable } = await filterDeliverableLeads(parsed.leads);

  const invalidEntries = [...parsed.invalidEntries, ...undeliverable];

  const result: IngestionResult = {
    addedCount: 0,
    repeatOrderCount: 0,
    duplicateCount: 0,
    invalidCount: invalidEntries.length,
    duplicates: [],
    invalidEntries,
    mailFailedCount: 0,
    failedEntries: [],
  };

  const mailContext = await loadSeminarMailContext();
  const total = deliverable.length;

  for (let i = 0; i < deliverable.length; i += 1) {
    const { email: normalizedEmail, name, orderCount } = deliverable[i];
    onProgress?.(i + 1, total);

    try {
      const units = Math.max(1, orderCount || 1);
      const upsert = await upsertCutierLead({
        email: normalizedEmail,
        name,
        monthKey,
        orderCount: units,
        source: 'bulk',
        mailContext,
      });

      result.addedCount += units;
      if (!upsert.isNew) {
        result.repeatOrderCount += units;
        result.duplicateCount = result.repeatOrderCount;
        result.duplicates.push(normalizedEmail);
      }
      if (upsert.isNew && !upsert.mailQueued) result.mailFailedCount++;
    } catch (err) {
      result.failedEntries.push({
        email: normalizedEmail,
        reason: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.',
      });
    }
  }

  return result;
}
